// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// IL RASTER DEI CONTROLLI DI QUALITÀ (ADR-029): luminanza alla FORMA del fotogramma, lato lungo 400 px, ridotta per MEDIA
// SU AREA (ogni pixel di arrivo è la media pesata dei pixel di partenza che copre). Isotropa: lo stesso fattore sui due assi,
// quindi un fotogramma in verticale (1200×1600 → 300×400) e lo stesso girato di 90° (1600×1200 → 400×300) danno gli stessi
// numeri. Prima il raster era sempre 400×300: con il video verticale la riduzione era 3× in larghezza e 5,3× in altezza.
// La media su area è definita senza ambiguità, a differenza di `drawImage` ridimensionato (dipende dal browser): è ciò che
// il server rifà sulla foto ricevuta, e un test con vettori noti confronta le due implementazioni.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Il lato lungo del raster dei controlli, in pixel. */
export const GATE_LONG_SIDE_PX = 400;

export interface RasterSize {
  widthPx: number;
  heightPx: number;
}

/** La forma del raster per un fotogramma: stesso rapporto, lato lungo 400 (mai più grande dell'originale). */
export function gateRasterSize(widthPx: number, heightPx: number, longSidePx = GATE_LONG_SIDE_PX): RasterSize {
  if (!(widthPx > 0) || !(heightPx > 0)) throw new Error(`Dimensioni non valide: ${widthPx}×${heightPx}`);
  const scale = Math.min(1, longSidePx / Math.max(widthPx, heightPx));
  return { widthPx: Math.max(1, Math.round(widthPx * scale)), heightPx: Math.max(1, Math.round(heightPx * scale)) };
}

/** Per ogni pixel di arrivo, i pixel di partenza che copre e il peso di ciascuno (la somma dei pesi è 1). */
function axisWeights(sourceLength: number, targetLength: number): { first: number[]; weights: number[][] } {
  const first: number[] = [];
  const weights: number[][] = [];
  const span = sourceLength / targetLength;
  for (let t = 0; t < targetLength; t++) {
    const start = t * span;
    const end = (t + 1) * span;
    const i0 = Math.floor(start);
    const i1 = Math.min(sourceLength, Math.ceil(end));
    const row: number[] = [];
    for (let i = i0; i < i1; i++) row.push((Math.min(end, i + 1) - Math.max(start, i)) / span);
    first.push(i0);
    weights.push(row);
  }
  return { first, weights };
}

/**
 * Riduzione per media su area, a righe: si danno le righe di partenza una alla volta (anche a fasce da `getImageData`) e in
 * memoria resta solo il raster di arrivo, non due fotogrammi pieni. Il risultato è arrotondato al byte più vicino.
 */
export class AreaReducer {
  private readonly cols: { first: number[]; weights: number[][] };
  private readonly rows: { first: number[]; weights: number[][] };
  private readonly acc: Float64Array;
  private readonly horizontal: Float64Array;
  private nextRow = 0;

  constructor(
    sourceWidthPx: number,
    private readonly sourceHeightPx: number,
    readonly target: RasterSize,
  ) {
    this.cols = axisWeights(sourceWidthPx, target.widthPx);
    this.rows = axisWeights(sourceHeightPx, target.heightPx);
    this.acc = new Float64Array(target.widthPx * target.heightPx);
    this.horizontal = new Float64Array(target.widthPx);
  }

  /** La prossima riga di luminanza del fotogramma di partenza (sourceWidthPx valori). */
  addRow(gray: ArrayLike<number>): void {
    const y = this.nextRow++;
    if (y >= this.sourceHeightPx) throw new Error('Più righe di quante ne abbia il fotogramma');
    const { widthPx: dw } = this.target;
    for (let x = 0; x < dw; x++) {
      const w = this.cols.weights[x]!;
      const i0 = this.cols.first[x]!;
      let s = 0;
      for (let k = 0; k < w.length; k++) s += w[k]! * gray[i0 + k]!;
      this.horizontal[x] = s;
    }
    // Questa riga di partenza contribuisce alle righe di arrivo che la coprono, ciascuna con il suo peso verticale.
    for (let ty = 0; ty < this.target.heightPx; ty++) {
      const first = this.rows.first[ty]!;
      const w = this.rows.weights[ty]!;
      if (y < first || y >= first + w.length) continue;
      const wy = w[y - first]!;
      const base = ty * dw;
      for (let x = 0; x < dw; x++) this.acc[base + x]! += wy * this.horizontal[x]!;
    }
  }

  result(): Uint8Array {
    if (this.nextRow !== this.sourceHeightPx) throw new Error(`Righe date ${this.nextRow} su ${this.sourceHeightPx}`);
    const out = new Uint8Array(this.acc.length);
    for (let i = 0; i < out.length; i++) out[i] = Math.round(this.acc[i]!);
    return out;
  }
}

/** La stessa riduzione su un fotogramma già tutto in memoria (i test, i vettori noti). */
export function reduceGray(gray: ArrayLike<number>, sourceWidthPx: number, sourceHeightPx: number, target: RasterSize = gateRasterSize(sourceWidthPx, sourceHeightPx)): Uint8Array {
  const reducer = new AreaReducer(sourceWidthPx, sourceHeightPx, target);
  const row = new Float64Array(sourceWidthPx);
  for (let y = 0; y < sourceHeightPx; y++) {
    for (let x = 0; x < sourceWidthPx; x++) row[x] = gray[y * sourceWidthPx + x]!;
    reducer.addRow(row);
  }
  return reducer.result();
}
